import React, { useEffect } from "react";

import { Box, Button, Container, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import MuiPhoneNumber from "material-ui-phone-number";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router";

import AddressInput from "api/types/AddressInput";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import useForm from "hooks/useForm";
import UserIcon from "icons/UserIcon";

import customerInfoValidate from "./validate";

const useStyles = makeStyles(theme => {
	return {
		container: {
			display: "flex",
			flexDirection: "column",
			padding: 0,
			paddingTop: theme.spacing(1)
		},
		titleRow: {
			marginBottom: theme.spacing(1),
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		titleBlock: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		icon: {
			marginRight: 10,
			fill: "#fff",
			[theme.breakpoints.up(480)]: {
				fill: theme.palette.text.primary
			}
		},
		title: {
			fontSize: 12,
			textTransform: "uppercase",
			fontWeight: 500,
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		},
		change: {
			border: 0,
			padding: 0,
			fontSize: 12,
			textTransform: "uppercase",
			fontWeight: 500,
			width: "auto",
			minWidth: "auto",
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		},
		data: {
			fontSize: 12,
			textTransform: "uppercase",
			paddingBottom: theme.spacing(1)
		},
		form: {
			padding: 0
		},
		textField: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			borderColor: "#fff",
			color: "#000",
			backgroundColor: "#f2f2f2",
			borderRadius: 8
		},
		phoneField: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			borderColor: "#fff",
			border: "none",
			color: "#000",
			backgroundColor: "#f2f2f2",
			borderRadius: 8
		}
	};
});

interface CustomerInfoProps {
	editMode?: boolean;
	fullName: string | undefined;
	deliveryAddress: AddressInput | null;
	phoneNumber: string | undefined;
	email: string | undefined;
	isSubmitting: boolean;
	onSubmit: (isValid: boolean) => void;
}

const CustomerInfo: React.FC<CustomerInfoProps> = ({
	editMode,
	fullName,
	deliveryAddress,
	phoneNumber,
	email,
	onSubmit,
	isSubmitting
}) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { orderType, saveCustomerInfo } = useCart();
	const { business } = useBusiness();
	const navigate = useNavigate();
	const { values, setValue, errors, onChange } = useForm(
		submitCallback,
		{ fullName, phoneNumber, email, deliveryAddress },
		values =>
			customerInfoValidate({
				fullName: values.fullName,
				phoneNumber: values.phoneNumber,
				email: values.email,
				deliveryAddress
			})
	);

	useEffect(() => {
		if (isSubmitting) {
			const isValid = Object.keys(errors).length === 0;
			submitCallback();
			if (isValid) {
				// setCustomerInfo({ ...values, deliveryAddress });
			} else {
				console.warn("errors", errors);
			}

			onSubmit(isValid);
		}
	}, [errors, isSubmitting, onSubmit, values]);

	function submitCallback() {}

	const onPhoneChanged = value => {
		setValue({ phoneNumber: value });
	};

	const onChangeClicked = () => {
		navigate("#checkout:1");
	};

	return (
		<Container className={classes.container}>
			<Box className={classes.titleRow}>
				<Box className={classes.titleBlock}>
					<UserIcon className={classes.icon} />
					<Typography className={classes.title}>{t("customerInformation")}</Typography>
				</Box>
				{editMode == null ? (
					<Button className={classes.change} onClick={onChangeClicked}>
						{t("change")}
					</Button>
				) : null}
			</Box>
			{editMode ? (
				<Box className={classes.form}>
					<TextField
						className={classes.textField}
						fullWidth
						placeholder={t("fullName")}
						variant="outlined"
						inputProps={{ maxLength: 80 }}
						id="fullName"
						name="fullName"
						required
						value={values.fullName}
						error={!!errors?.fullName}
						onChange={onChange}
					/>
					{/* {orderType?.orderType !== AvailableOrderTypes.pickup ? (
						<PlacesAutocomplete
							className={classes.textField}
							placeholder={t("deliveryAddress")}
							defaultValue={deliveryAddress?.place}
							origin={business?.address?.coordinates}
							radius={orderType?.deliveryRadiusMeters || undefined}
							onChange={(event, value) => {
								setValue({
									deliveryAddress: value && {
										address1: value.structured_formatting.main_text,
										address2: null,
										address3: null,
										city: value.structured_formatting.secondary_text,
										state: null,
										postalCode: null,
										country: null,
										placeId: value.place_id,
										place: value
									}
								});
							}}
						/>
					) : null} */}
					<MuiPhoneNumber
						className={classes.phoneField}
						inputProps={{ className: classes.phoneField, placeholder: t("mobilePhone") }}
						defaultCountry={"us"}
						onlyCountries={["us"]}
						disableCountryCode
						disableDropdown
						fullWidth
						required
						id="phoneNumber"
						name="phoneNumber"
						value={values.phoneNumber}
						onChange={onPhoneChanged}
						error={errors?.mobilePhone ? true : false}
						variant="outlined"
					/>

					<TextField
						className={classes.textField}
						fullWidth
						placeholder={t("email")}
						inputMode={"email"}
						id="email"
						name="email"
						value={values.email}
						onChange={onChange}
						variant="outlined"
						inputProps={{ maxLength: 200 }}
					/>
				</Box>
			) : (
				<>
					<Box className={classes.data}>{fullName}</Box>
					<Box className={classes.data}>{phoneNumber}</Box>
					<Box className={classes.data}>{email}</Box>
				</>
			)}
		</Container>
	);
};

export default CustomerInfo;
